import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";

// components
import { SiteSettings } from "../components/site-settings/site-settings";
import { LoaderMUI } from "../assets/global/loader/loader-mui";
import { AlertMessage } from "../assets/global/alert/alert-message";

// services
import { calculateBuildingMetrics } from "../utils/planner";
import { fetchBuildings } from "../services/api/planner-api";
import { fetchProperty } from "../services/api/site-settings-api";
import { parseJSON } from "../utils/formatting";
import MapContext from "../services/context/map-context";
import { getCentroidCoordinates } from "../utils/map-labels";
import { useAppContext } from "../services/context/app-context";
import { PROPERTY_SOURCE, UNITS_DEFAULT } from "../constants/site-settings.constants";

export const SiteSettingsPage = () => {
  const { projectId } = useParams();
  const { setPropertyLocation } = useAppContext();
  const { map, mapLoaded } = useContext(MapContext);

  const [property, setProperty] = useState(null);
  const [area, setArea] = useState(null);
  const [buildHeight, setBuildHeight] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Run all API calls concurrently.
        const [fetchedBuildings, fetchedProperty] = await Promise.all([
          fetchBuildings(projectId),
          fetchProperty(projectId),
        ]);

        const { buildingAreaSum, maxInnerBuildHeight } =
          calculateBuildingMetrics(fetchedBuildings);

        setArea(buildingAreaSum);
        setBuildHeight(maxInnerBuildHeight);

        if (fetchedProperty && Object.hasOwn(fetchedProperty, "geodata")) {
          const propertyGeoData = parseJSON(fetchedProperty?.geodata)
            ? {
                type: "FeatureCollection",
                features: [parseJSON(fetchedProperty.geodata)],
              }
            : null;

          /*  const propertyParsed = {
          ...fetchedProperty,
          access1: parseJSON(fetchedProperty.access1),
          geodata: propertyGeoData,
          locationGeodata: parseJSON(fetchedProperty.locationGeodata),
        }; */

        setPropertyLocation([fetchedProperty.coordinates?._longitude, fetchedProperty.coordinates?._latitude]);

        const isFile = fetchedProperty.siteSource === PROPERTY_SOURCE.FILE;

          setProperty({
            location: fetchedProperty.location,
            locationGeodata: parseJSON(fetchedProperty.locationGeodata),
            propertyGeoJSON: propertyGeoData,
            selectedLine: parseJSON(fetchedProperty.access1),
            area: fetchedProperty.area,
            perimeter: fetchedProperty.perimeter,
            minBorderDistance: fetchedProperty.minBorderDistance,
            maxConstrHeight: fetchedProperty.maxConstrHeight,
            buildingDensity: fetchedProperty.buildingDensity,
            borderWidths: {},
            coordinatesCenter: {
              lat: fetchedProperty.coordinates?._latitude,
              long: fetchedProperty.coordinates?._longitude,
            },
            maxBorder: null,
            propertyArea: fetchedProperty.propertyArea,
            propertySource: fetchedProperty.siteSource ? fetchedProperty.siteSource : PROPERTY_SOURCE.HAND_DRAWN,
            fileId: isFile ? fetchedProperty.fileId : null,
            fileName: isFile ? fetchedProperty.fileName : null,
            units: isFile ? fetchedProperty.units : UNITS_DEFAULT,
            center: isFile ? fetchedProperty.center : null,
            /* pivot: isFile ? [fetchedProperty.center.long, fetchedProperty.center.lat] : null, */
            /* bearing: 0,
            angle: 0, */
            uploadedPropertyGeoJSON: isFile ? propertyGeoData : null,
            uploadedPropertyArea: isFile ? fetchedProperty.propertyArea : null,
            uploadedPropertyPerimeter: isFile ? fetchedProperty.perimeter : null,
            uploadedFileName: isFile ? fetchedProperty.fileName : null,
            uploadedUnits: isFile ? fetchedProperty.units : null,
          });

          if (map && mapLoaded && propertyGeoData) {
            // Fit the map to the property
            const coordinates = getCentroidCoordinates(
              propertyGeoData.features[0]
            );
            if (coordinates) {
              map.flyTo({ center: coordinates, zoom: 17 });
            }
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        const message =
          err.response?.data?.message || err.message || "Error fetching data";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  return (
    <>
      {/* Loader */}
      {loading && <LoaderMUI withBackdrop={true} />}

      {/* Error Alert */}
      {error && <AlertMessage message={error} type="error" />}

      {!loading && !error && (
        <SiteSettings
          existedProperty={property}
          buildingArea={area}
          maxInnerBuildHeight={buildHeight}
        />
      )}
    </>
  );
};
